// apps/client/src/utils/commissionTree.ts
//
// Pure grouping and totalling for the Commission Settings loan tree and the
// reconciliation table. Both BrokerCommissionSettingsPage (admin) and
// BrokerCommissionViewPage (broker) build their year > month > payment tree
// through here so the two surfaces cannot drift apart again.
//
// History (2026-09-07). From 2026-03-27 both pages collapsed the tree to ONE
// row per loanId::paymentType, keeping only the latest month, on the premise
// that Loan Market trail files "carry forward" upfront records. The server
// already returns one row per (loan_id, period_month, payment_type) and the
// pipeline dedups by logical key, so the collapse threw away real money:
// every earlier trail month, every Subsequent Upfront top-up, every clawback
// or reversal that was not the latest row. The tiles and the reconciliation
// table above the tree never deduped, so the same page contradicted itself
// ($108,253.01 above vs $101,230.16 below for one Entourage broker; $142,343.14
// vs $115,782.50 for Sam Landis at Double Bay). Every payment row now renders,
// and the tree, the reconciliation and the tiles all run on the Australian
// financial year so there is one clock on the page.
//
// All money is integer cents. Never use floats.

export interface TreeRowBase {
  loanId: string;
  paymentType: string;
  /** 'YYYY-MM' period month (payment period, not settlement). */
  month: string;
  /** Gross commission before the broker split. */
  commissionCents: number;
  /** The broker's share for this row; each page decides its own basis. */
  brokerShareCents: number;
  isUntagged: boolean;
  isArrears: boolean;
  isDischarged: boolean;
  isReferrerDeduction: boolean;
  isManualAdjustment?: boolean;
  manualAdjustmentId?: string;
}

export interface TreeMonthGroup<T extends TreeRowBase> {
  /** 'YYYY-MM' */
  month: string;
  loans: T[];
  totalCommsCents: number;
  totalBrokerShareCents: number;
  /** Payment rows in this month. NOT loans: a loan paying upfront and trail
   *  in the same month is two payments. */
  paymentCount: number;
  /** Distinct loan ids in this month, manual adjustments excluded. */
  loanCount: number;
  untaggedCount: number;
  arrearsCount: number;
  dischargedCount: number;
  referrerDeductionCount: number;
  /** True when every row in the month carries a visible gross figure. */
  grossTotalVisible: boolean;
}

export interface TreeYearGroup<T extends TreeRowBase> {
  /** Australian financial year, identified by its END calendar year:
   *  Jul 2026 to Jun 2027 is 2027, labelled "FY27". */
  year: number;
  label: string;
  rangeLabel: string;
  months: TreeMonthGroup<T>[];
  totalCommsCents: number;
  totalBrokerShareCents: number;
  paymentCount: number;
  loanCount: number;
  grossTotalVisible: boolean;
}

export interface BuildTreeOptions<T extends TreeRowBase> {
  /** Which figure the subtotals sum. Defaults to `brokerShareCents`. */
  shareOf?: (row: T) => number;
  /** Row order inside a month. Defaults to loanId ascending. */
  sortLoans?: (a: T, b: T) => number;
  /** Whether a row's gross figure is visible to the viewer. Defaults to true. */
  grossVisible?: (row: T) => boolean;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** FY end year for a 'YYYY-MM' month: '2026-07' -> 2027, '2026-06' -> 2026. */
export function fiscalYearOf(month: string): number {
  const [y, m] = month.split('-').map(Number);
  return m >= 7 ? y + 1 : y;
}

/** "FY27" for fiscal year 2027. */
export function fiscalYearLabel(fy: number): string {
  return `FY${String(fy % 100).padStart(2, '0')}`;
}

/** "Jul 2026 to Jun 2027" for fiscal year 2027. */
export function fiscalYearRange(fy: number): string {
  return `Jul ${fy - 1} to Jun ${fy}`;
}

/** The fiscal year `now` falls in. Injectable for tests. */
export function currentFiscalYear(now: Date = new Date()): number {
  return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
}

export function isInFiscalYear(month: string, fy: number): boolean {
  return fiscalYearOf(month) === fy;
}

/** "Jul 2026" for '2026-07'. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

/** Newest 'YYYY-MM' among the rows, or null when there are none. */
export function latestMonth(rows: ReadonlyArray<{ month: string }>): string | null {
  let latest: string | null = null;
  for (const r of rows) if (latest === null || r.month > latest) latest = r.month;
  return latest;
}

/** Distinct loan ids, manual adjustments excluded. */
export function distinctLoanCount(rows: ReadonlyArray<TreeRowBase>): number {
  const ids = new Set<string>();
  for (const r of rows) if (!r.isManualAdjustment) ids.add(r.loanId);
  return ids.size;
}

/** Stable identity for a payment row. A loan paying in several months, or
 *  paying two legs in one month, is several rows and each needs its own key. */
export function paymentRowKey(row: TreeRowBase): string {
  if (row.isManualAdjustment && row.manualAdjustmentId) return `manual:${row.manualAdjustmentId}`;
  return `${row.loanId}::${row.paymentType}::${row.month}`;
}

/**
 * Group payment rows into fiscal year > month > rows. EVERY row passed in is
 * kept; filtering is the caller's job and happens before this call, so a
 * search or tag filter changes the tree subtotals (labelled as filtered by
 * the caller) and nothing else.
 */
export function buildFiscalYearGroups<T extends TreeRowBase>(
  rows: ReadonlyArray<T>,
  opts: BuildTreeOptions<T> = {},
): TreeYearGroup<T>[] {
  const shareOf = opts.shareOf ?? ((r: T) => r.brokerShareCents);
  const sortLoans = opts.sortLoans ?? ((a: T, b: T) => a.loanId.localeCompare(b.loanId));
  const grossVisible = opts.grossVisible ?? (() => true);

  const byYear = new Map<number, Map<string, T[]>>();
  for (const row of rows) {
    const fy = fiscalYearOf(row.month);
    if (!byYear.has(fy)) byYear.set(fy, new Map());
    const byMonth = byYear.get(fy)!;
    if (!byMonth.has(row.month)) byMonth.set(row.month, []);
    byMonth.get(row.month)!.push(row);
  }

  const groups: TreeYearGroup<T>[] = [];
  for (const [fy, byMonth] of byYear) {
    const months: TreeMonthGroup<T>[] = [];
    for (const [month, loans] of byMonth) {
      months.push({
        month,
        loans: [...loans].sort(sortLoans),
        totalCommsCents: loans.reduce((s, l) => s + l.commissionCents, 0),
        totalBrokerShareCents: loans.reduce((s, l) => s + shareOf(l), 0),
        paymentCount: loans.length,
        loanCount: distinctLoanCount(loans),
        untaggedCount: loans.filter(l => l.isUntagged).length,
        arrearsCount: loans.filter(l => l.isArrears).length,
        dischargedCount: loans.filter(l => l.isDischarged).length,
        referrerDeductionCount: loans.filter(l => l.isReferrerDeduction).length,
        grossTotalVisible: loans.every(grossVisible),
      });
    }
    months.sort((a, b) => b.month.localeCompare(a.month));
    const all = months.flatMap(m => m.loans);
    groups.push({
      year: fy,
      label: fiscalYearLabel(fy),
      rangeLabel: fiscalYearRange(fy),
      months,
      totalCommsCents: months.reduce((s, m) => s + m.totalCommsCents, 0),
      totalBrokerShareCents: months.reduce((s, m) => s + m.totalBrokerShareCents, 0),
      paymentCount: all.length,
      loanCount: distinctLoanCount(all),
      grossTotalVisible: months.every(m => m.grossTotalVisible),
    });
  }
  groups.sort((a, b) => b.year - a.year);
  return groups;
}

export interface FiscalYearBucket<T extends { month: string }> {
  year: number;
  label: string;
  rangeLabel: string;
  /** Descending by month. */
  months: T[];
}

/** Group any month-keyed rows (the reconciliation table) by fiscal year,
 *  newest year first, months descending inside each year. */
export function groupByFiscalYear<T extends { month: string }>(items: ReadonlyArray<T>): FiscalYearBucket<T>[] {
  const map = new Map<number, T[]>();
  for (const item of items) {
    const fy = fiscalYearOf(item.month);
    if (!map.has(fy)) map.set(fy, []);
    map.get(fy)!.push(item);
  }
  const out: FiscalYearBucket<T>[] = [];
  for (const [fy, months] of map) {
    out.push({
      year: fy,
      label: fiscalYearLabel(fy),
      rangeLabel: fiscalYearRange(fy),
      months: [...months].sort((a, b) => b.month.localeCompare(a.month)),
    });
  }
  return out.sort((a, b) => b.year - a.year);
}

export interface ReconciliationLike {
  month: string;
  brokerCommsCents: number;
  baseSalaryCents: number;
  totalPaidCents: number;
  amtOnTopOfBaseCents: number;
  carryOverCents: number;
}

export interface FiscalYearTotals {
  commsCents: number;
  baseSalaryCents: number;
  totalPaidCents: number;
  amtOnTopCents: number;
  /** Running balance at the last loaded month of the year, not a sum. */
  carryOverCents: number;
  /** How many months of the year have data. */
  monthsLoaded: number;
  /** Newest month with data in the year, or null. */
  latestMonth: string | null;
}

/** The headline tile and the "FY to date" footer row. Carry-over is a
 *  balance, so it is the LAST month's value, never a sum. */
export function sumFiscalYear(recon: ReadonlyArray<ReconciliationLike>, fy: number): FiscalYearTotals {
  const inYear = recon.filter(r => isInFiscalYear(r.month, fy)).sort((a, b) => a.month.localeCompare(b.month));
  return {
    commsCents: inYear.reduce((s, r) => s + r.brokerCommsCents, 0),
    baseSalaryCents: inYear.reduce((s, r) => s + r.baseSalaryCents, 0),
    totalPaidCents: inYear.reduce((s, r) => s + r.totalPaidCents, 0),
    amtOnTopCents: inYear.reduce((s, r) => s + r.amtOnTopOfBaseCents, 0),
    carryOverCents: inYear[inYear.length - 1]?.carryOverCents ?? 0,
    monthsLoaded: inYear.length,
    latestMonth: inYear[inYear.length - 1]?.month ?? null,
  };
}

/** "FY27 · Jul 2026 to Jul 2026 · 1 month loaded" for the tile detail line. */
export function fiscalYearToDateDetail(fy: number, totals: Pick<FiscalYearTotals, 'monthsLoaded' | 'latestMonth'>): string {
  const label = fiscalYearLabel(fy);
  if (!totals.latestMonth) return `${label} · no commission data loaded yet`;
  const from = `Jul ${fy - 1}`;
  const n = totals.monthsLoaded;
  return `${label} · ${from} to ${monthLabel(totals.latestMonth)} · ${n} month${n === 1 ? '' : 's'} loaded`;
}
